import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { toast } from 'react-toastify';
import { QR_CODE_EXPIRATION_MS, ZAPI_STATUS_POLL_INTERVAL_MS } from '../../Atendabot/constants/timing';
import {
  cancelZapiInstance,
  getZapiInstances,
  registerZapiInstance,
  refreshZapiInstance,
  refreshZapiQrCode,
  subscribeZapiInstance,
  migrateZapiInstance,
} from '../services/api';

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const TABLE_SX = {
  width: '100%',
  tableLayout: 'fixed',
  '& th': {
    bgcolor: '#f8fafc',
    color: '#475467',
    fontSize: '0.775rem',
    fontWeight: 700,
    py: 1.7,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  '& td': {
    py: 1.65,
    color: '#344054',
    borderColor: '#f1f5f9',
    verticalAlign: 'middle',
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
  },
  '& .MuiChip-root': { borderRadius: '8px', fontWeight: 600, maxWidth: '100%', height: 'auto', minHeight: 24 },
  '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 },
  '& .MuiButton-root': { borderRadius: '8px', fontWeight: 700, whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 0, maxWidth: '100%' },
};

const STATUS_LABELS = {
  desconhecida: 'Aguardando sincronização',
  criada: 'Criada',
  aguardando_qr: 'Aguardando QR',
  conectada: 'Conectada',
  expirada: 'Expirada',
  cancelada: 'Cancelada',
};

const INACTIVE_STATUSES = ['cancelada', 'expirada'];
const INACTIVE_PROVIDER_STATUSES = [
  'CANCELED',
  'CANCELLED',
  'DISABLED',
  'EXPIRED',
  'INACTIVE',
  'TRIAL_EXPIRED',
];
const isInactiveInstance = (instance) => (
  INACTIVE_STATUSES.includes(String(instance?.status || '').toLowerCase())
  || INACTIVE_PROVIDER_STATUSES.includes(String(instance?.provider_status || '').toUpperCase())
);
const isConnectedInstance = (instance) => instance?.status === 'conectada';
const hasDeferredCancellation = (instance) => Boolean(
  instance?.cancel_requested_at && instance?.cancel_reason === 'manager_period_end'
);

const statusColor = (status) => {
  if (status === 'conectada') return 'success';
  if (status === 'aguardando_qr' || status === 'criada') return 'warning';
  if (status === 'cancelada' || status === 'expirada') return 'error';
  return 'default';
};

const providerPayment = (instance) => {
  if (instance.provider === 'uazapi') {
    if (instance.cancellation_pending) return { label: 'Remoção em andamento', color: 'warning', variant: 'outlined' };
    if (isInactiveInstance(instance)) return { label: 'Inativa', color: 'default', variant: 'outlined' };
    if (instance.provider_status === 'FIXED_POOL') return { label: 'Plano fixo', color: 'primary', variant: 'outlined' };
    return { label: instance.is_subscribed ? 'Assinatura ativa' : 'Trial da ferramenta', color: 'primary', variant: 'outlined' };
  }
  if (hasDeferredCancellation(instance) && !isInactiveInstance(instance)) {
    return { label: 'Renovação cancelada · ciclo vigente', color: 'warning', variant: 'outlined' };
  }
  const status = String(instance?.provider_status || '').trim().toUpperCase();
  if (['ACTIVE', 'PAID', 'SUBSCRIBED'].includes(status)) {
    return { label: 'Assinada', color: 'primary', variant: 'filled' };
  }
  if (status === 'TRIAL') {
    return { label: 'Trial', color: 'info', variant: 'outlined' };
  }
  if (['CANCELLATION_PROCESS', 'CANCELLATION_PENDING', 'CANCEL_REQUESTED'].includes(status)) {
    return { label: 'Cancelamento em andamento', color: 'warning', variant: 'outlined' };
  }
  if (['CANCELED', 'CANCELLED'].includes(status)) {
    return { label: 'Cancelada', color: 'error', variant: 'outlined' };
  }
  if (status === 'TRIAL_EXPIRED') {
    return { label: 'Trial expirado', color: 'error', variant: 'outlined' };
  }
  if (status) {
    return { label: status, color: 'default', variant: 'outlined' };
  }
  return {
    label: instance?.is_subscribed ? 'Assinada' : 'Não informada',
    color: instance?.is_subscribed ? 'primary' : 'default',
    variant: instance?.is_subscribed ? 'filled' : 'outlined',
  };
};

const providerDueDate = (instance) => (
  instance?.provider_expires_at || instance?.legacy_expires_at
  || (!instance?.is_subscribed ? instance?.trial_expires_at : null)
);

const formatProviderDueDate = (instance) => {
  const dueAt = providerDueDate(instance);
  if (!dueAt) return '—';
  const date = new Date(dueAt);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
};

const normalizeQrImage = (payload) => {
  const candidates = [
    payload,
    payload?.image,
    payload?.image?.value,
    payload?.value,
    payload?.base64,
    payload?.data,
  ];
  const image = candidates.find((candidate) => (
    typeof candidate === 'string' && candidate.trim().length > 0
  ));
  if (!image) return null;
  const value = image.trim();
  return /^(data:image\/|https?:\/\/)/i.test(value) ? value : `data:image/png;base64,${value}`;
};

const normalizeSearch = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const QR_IMAGE_STYLE = { width: 280, maxWidth: '100%', padding: 16, boxSizing: 'border-box', background: '#fff' };

const ZapiInstancesPage = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [qrTarget, setQrTarget] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);
  const [qrConnected, setQrConnected] = useState(false);
  const [confirmationTarget, setConfirmationTarget] = useState(null);
  const [migrationTarget, setMigrationTarget] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationImage, setMigrationImage] = useState(null);
  const [migrationError, setMigrationError] = useState('');
  const [migrationBusy, setMigrationBusy] = useState(false);
  const [localExpanded, setLocalExpanded] = useState(true);
  const [localStatusFilter, setLocalStatusFilter] = useState('active');
  const [nameFilter, setNameFilter] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const connectionStatuses = useRef({});
  const [registerForm, setRegisterForm] = useState({
    name: '',
    product_code: 'atendabot',
    restaurant_id: '',
    instance_id: '',
    instance_token: '',
    legacy_expires_at: '',
  });

  const loadInstances = useCallback(async ({ showLoader = true } = {}) => {
    if (showLoader) setLoading(true);
    try {
      const rows = await getZapiInstances();
      setInstances(rows.map(row => row.provider === 'uazapi' && row.configured
        ? { ...row, ...connectionStatuses.current[row.instance_id] } : row));
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao carregar inventário local.');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => { loadInstances(); }, [loadInstances]);

  useEffect(() => {
    if (!qrTarget || qrLoading || qrConnected) return undefined;
    let active = true;
    let polling = false;
    const intervalId = window.setInterval(async () => {
      if (polling) return;
      polling = true;
      try {
        const result = await refreshZapiInstance(qrTarget.instance_id);
        if (!active) return;
        if (qrTarget.provider === 'uazapi') connectionStatuses.current[qrTarget.instance_id] = {
          status: result.status, connected: result.connected,
        };
        setInstances(current => current.map(instance => instance.instance_id === qrTarget.instance_id
          ? { ...instance, ...result } : instance));
        if (result.connected) {
          setQrImage(null);
          setQrError(null);
          setQrConnected(true);
        }
      } catch (_) {
        // Uma falha temporária não interrompe a próxima consulta.
      } finally {
        polling = false;
      }
    }, ZAPI_STATUS_POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [qrTarget, qrLoading, qrConnected]);

  useEffect(() => {
    if (!qrImage) return undefined;
    const timeoutId = window.setTimeout(() => {
      setQrImage(null);
      setQrError('O QR Code expirou. Gere um novo para conectar.');
    }, QR_CODE_EXPIRATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [qrImage]);

  useEffect(() => {
    if (!migrationTarget || !migrationImage) return undefined;
    const timeoutId = window.setTimeout(() => {
      setMigrationImage(null);
      setMigrationError('O QR Code expirou. Selecione Mostrar QR Code para gerar outro.');
    }, QR_CODE_EXPIRATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [migrationTarget, migrationImage]);

  const visibleInstances = useMemo(() => {
    const search = normalizeSearch(nameFilter);
    return instances.filter((instance) => {
      const names = [instance.name, instance.restaurant?.nome,
        ...(instance.detected_usages || []).map(usage => usage.restaurant_name)];
      if (search && !names.some(name => normalizeSearch(name).includes(search))) return false;
      if (localStatusFilter === 'all') return true;
      const inactive = isInactiveInstance(instance);
      return localStatusFilter === 'inactive' ? inactive : !inactive;
    });
  }, [instances, localStatusFilter, nameFilter]);

  const totals = useMemo(() => ({
    total: visibleInstances.length,
    connected: visibleInstances.filter(isConnectedInstance).length,
    subscribed: visibleInstances.filter((item) => item.is_subscribed).length,
  }), [visibleInstances]);

  const emptyLocalMessage = nameFilter.trim()
    ? 'Nenhuma instância encontrada com esse nome e status.'
    : localStatusFilter === 'active'
      ? 'Nenhuma instância ativa cadastrada.'
      : localStatusFilter === 'inactive'
        ? 'Nenhuma instância cancelada ou expirada cadastrada.'
        : 'Nenhuma instância cadastrada.';

  const runAction = async (instanceId, action, successMessage) => {
    setActionId(instanceId);
    try {
      delete connectionStatuses.current[instanceId];
      await action(instanceId);
      toast.success(successMessage);
      await loadInstances({ showLoader: false });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Ação não concluída.');
    } finally {
      setActionId(null);
    }
  };

  const refreshVisibleStatuses = async () => {
    if (!visibleInstances.length) return;
    setRefreshingStatuses(true);
    const results = await Promise.allSettled(
      visibleInstances.map(async (instance) => {
        const result = await refreshZapiInstance(instance.instance_id);
        if (instance.provider === 'uazapi') connectionStatuses.current[instance.instance_id] = {
          status: result.status, connected: result.connected,
        };
        return result;
      }),
    );
    const failures = results.filter((result) => result.status === 'rejected').length;
    await loadInstances({ showLoader: false });
    if (failures) {
      toast.error(`${failures} instância${failures > 1 ? 's não puderam' : ' não pôde'} ser atualizada.`);
    } else {
      toast.success('Status das instâncias atualizado.');
    }
    setRefreshingStatuses(false);
  };

  const showQrCode = async (instance) => {
    const instanceId = instance.instance_id;
    setQrTarget(instance);
    setQrImage(null);
    setQrError(null);
    setQrConnected(false);
    setQrLoading(true);
    setActionId(instanceId);
    try {
      const data = await refreshZapiQrCode(instanceId);
      const image = normalizeQrImage(data);
      if (!image) throw new Error('O provedor não retornou uma imagem de QR Code.');
      setQrImage(image);
    } catch (error) {
      const message = error?.response?.data?.error
        || error?.message
        || 'QR Code indisponível.';
      setQrError(message);
      toast.error(message);
    } finally {
      setQrLoading(false);
      setActionId(null);
    }
  };

  const closeQrCode = () => {
    setQrTarget(null);
    setQrImage(null);
    setQrError(null);
    setQrConnected(false);
  };

  const confirmInstanceAction = async () => {
    const target = confirmationTarget;
    if (!target) return;
    setConfirmationTarget(null);
    if (target.type === 'subscribe') {
      await runAction(target.instance.instance_id, subscribeZapiInstance, 'Assinatura solicitada.');
      return;
    }
    await runAction(target.instance.instance_id, cancelZapiInstance, 'Cancelamento solicitado.');
  };

  const submitLegacyInstance = async () => {
    setRegistering(true);
    try {
      await registerZapiInstance({
        ...registerForm,
        restaurant_id: registerForm.restaurant_id || null,
        legacy_expires_at: registerForm.legacy_expires_at || null,
      });
      toast.success('Instância existente cadastrada e validada.');
      setRegisterOpen(false);
      setRegisterForm({
        name: '',
        product_code: 'atendabot',
        restaurant_id: '',
        instance_id: '',
        instance_token: '',
        legacy_expires_at: '',
      });
      await loadInstances({ showLoader: false });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Não foi possível validar a instância.');
    } finally {
      setRegistering(false);
    }
  };

  const confirmingSubscription = confirmationTarget?.type === 'subscribe';
  const confirmingUazapi = confirmationTarget?.instance?.provider === 'uazapi';
  const runMigration = async (action) => {
    setMigrationBusy(true);
    setMigrationError('');
    if (action === 'qr-code') setMigrationImage(null);
    try {
      const result = await migrateZapiInstance(migrationTarget.instance_id, action);
      if (action === 'qr-code') {
        const image = normalizeQrImage(result);
        if (!image) throw new Error('QR Code indisponível. Atualize o status da conexão.');
        setMigrationImage(image);
      } else {
        setMigrationStatus(result);
        if (result.connected) setMigrationImage(null);
      }
      if (action === 'complete') {
        toast.success('Conexão migrada para a UAZAPI. O cancelamento da Z-API é uma ação separada.');
        await loadInstances({ showLoader: false });
      }
    } catch (error) {
      setMigrationError(error?.response?.data?.error || error.message || 'Não foi possível concluir a operação.');
    } finally {
      setMigrationBusy(false);
    }
  };
  const actionsDisabled = Boolean(actionId) || refreshingStatuses;

  const renderActions = (instance, mobile = false) => {
    const inactive = isInactiveInstance(instance);
    const isUazapi = instance.provider === 'uazapi';
    const migrated = instance.whatsapp_provider === 'uazapi' || instance.restaurant?.atendabot_method === 'uazapi';
    const showQr = !isConnectedInstance(instance) && !inactive && (isUazapi
      ? instance.configured && instance.activation_allowed !== false : !migrated);
    const showSubscribe = isUazapi ? !instance.configured && !instance.cancellation_pending && instance.activation_allowed
      : !instance.is_subscribed && instance.status !== 'expirada';
    const showCancel = isUazapi ? instance.configured || instance.cancellation_pending : !inactive && !hasDeferredCancellation(instance);
    const showMigrate = !isUazapi && !migrated && ((instance.product_code === 'atendabot' && instance.restaurant_id)
      || instance.migration_product
      || (instance.detected_usages || []).some((usage) => ['reservo', 'clube_mesa'].includes(usage.product_code)));
    if (!showQr && !showSubscribe && !showCancel && !showMigrate) {
      return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    return (
      <Stack
        direction="row"
        gap={1}
        justifyContent={mobile ? 'flex-start' : 'flex-end'}
        flexWrap="wrap"
        useFlexGap
        sx={{ minWidth: 0, maxWidth: '100%', '& .MuiButton-root': { whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 0, maxWidth: '100%' } }}
      >
        {showMigrate && (
          <Button
            size="small"
            variant="outlined"
            disabled={actionsDisabled}
            onClick={() => {
              setMigrationTarget(instance);
              setMigrationStatus(null);
              setMigrationImage(null);
              setMigrationError('');
            }}
          >
            Preparar UAZAPI
          </Button>
        )}
        {showQr && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<QrCode2OutlinedIcon />}
            onClick={() => showQrCode(instance)}
            disabled={actionsDisabled}
          >
            QR Code
          </Button>
        )}
        {showSubscribe && (
          <Button
            color="primary"
            size="small"
            variant="outlined"
            startIcon={<VerifiedOutlinedIcon />}
            onClick={() => setConfirmationTarget({ type: 'subscribe', instance })}
            disabled={actionsDisabled}
          >
            {isUazapi ? 'Ativar UAZAPI' : 'Assinar Z-API'}
          </Button>
        )}
        {showCancel && (
          <Button
            color="error"
            size="small"
            variant="outlined"
            startIcon={<CancelOutlinedIcon />}
            onClick={() => setConfirmationTarget({ type: 'cancel', instance })}
            disabled={actionsDisabled}
          >
            {isUazapi ? instance.cancellation_pending ? 'Concluir remoção UAZAPI' : 'Cancelar UAZAPI' : 'Cancelar Z-API'}
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Box sx={{ ...CARD_SX, mb: 3 }}>
          <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            gap={2}
            sx={{ p: { xs: 2.5, md: 3 } }}
          >
            <Box>
              <Typography variant="h5" fontWeight={800}>Instâncias de WhatsApp</Typography>
              <Typography color="text.secondary">Instâncias cadastradas e utilizadas pelos produtos da plataforma.</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => setRegisterOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Cadastrar existente
            </Button>
          </Stack>
        </Box>

        <Box sx={CARD_SX}>
          <Box sx={{ p: { xs: 2, md: 2.5 }, pb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              gap={2}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight={700}>Instâncias da plataforma</Typography>
                  <Tooltip title="UAZAPI usa as vagas do plano fixo; o acesso segue a assinatura ou o trial da ferramenta. Na Z-API, assinar gera cobrança e cancelar a renovação preserva o ciclo vigente. Preparar UAZAPI mantém a Z-API e o cadastro do AtendaBot." enterTouchDelay={0} describeChild>
                    <IconButton size="small" aria-label="Sobre os provedores e cobranças">
                      <HelpOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Chip label={`${totals.total} instâncias`} />
                  <Chip color="success" label={`${totals.connected} conectadas`} />
                  <Chip color="primary" label={`${totals.subscribed} assinadas`} />
                </Stack>
              </Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                gap={1}
                flexWrap="wrap"
                useFlexGap
                width={{ xs: '100%', md: 'auto' }}
              >
                <TextField
                  label="Buscar por nome"
                  size="small"
                  value={nameFilter}
                  onChange={(event) => setNameFilter(event.target.value)}
                  inputProps={{ 'aria-label': 'Buscar instância ou restaurante por nome' }}
                  sx={{ minWidth: 190, flex: 1 }}
                />
                <TextField
                  select
                  label="Exibir"
                  size="small"
                  value={localStatusFilter}
                  onChange={(event) => setLocalStatusFilter(event.target.value)}
                  SelectProps={{ native: true }}
                  inputProps={{ 'aria-label': 'Filtrar inventário local' }}
                  sx={{ minWidth: 190 }}
                >
                  <option value="active">Somente ativas</option>
                  <option value="inactive">Canceladas e expiradas</option>
                  <option value="all">Todas</option>
                </TextField>
                <Button
                  variant="outlined"
                  startIcon={refreshingStatuses
                    ? <CircularProgress size={16} />
                    : <RefreshOutlinedIcon />}
                  onClick={refreshVisibleStatuses}
                  disabled={loading || refreshingStatuses || !visibleInstances.length || Boolean(actionId)}
                  sx={{ minHeight: 40, whiteSpace: 'normal' }}
                >
                  Atualizar status
                </Button>
                <IconButton
                  onClick={() => setLocalExpanded((current) => !current)}
                  aria-label={localExpanded ? 'Recolher inventário local' : 'Expandir inventário local'}
                  aria-expanded={localExpanded}
                  size="small"
                >
                  {localExpanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          <Collapse in={localExpanded} timeout="auto" unmountOnExit>
            <Divider />

            <TableContainer sx={{ display: { xs: 'none', md: 'block' }, px: 1.5, py: 1.5, boxSizing: 'border-box' }}>
              <Table size="small" sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell>Instância</TableCell>
                    <TableCell>Provedor</TableCell>
                    <TableCell>Produto e vínculo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ width: '16%' }}>Pagamento / acesso</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell align="right" sx={{ width: '20%' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !instances.length ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : visibleInstances.map((instance) => {
                    const payment = providerPayment(instance);
                    return (
                      <TableRow key={instance.instance_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>{instance.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{instance.instance_id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={instance.provider === 'uazapi' ? 'UAZAPI' : 'Z-API'} />
                          {instance.is_current !== undefined && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {instance.is_current ? 'Em uso pelo produto' : 'Conexão alternativa'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{instance.product_code}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {instance.restaurant?.nome
                              || (instance.detected_usages || []).map((usage) => (
                                usage.restaurant_name || usage.product_code
                              )).join(', ')
                              || 'Sem vínculo com restaurante'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={statusColor(instance.status)}
                            label={STATUS_LABELS[instance.status] || instance.status}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={payment.color}
                            variant={payment.variant}
                            label={payment.label}
                          />
                        </TableCell>
                        <TableCell>{formatProviderDueDate(instance)}</TableCell>
                        <TableCell align="right">{renderActions(instance)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && !visibleInstances.length && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>{emptyLocalMessage}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: { xs: 'block', md: 'none' }, '& .MuiChip-root': { maxWidth: '100%', height: 'auto', minHeight: 24 }, '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 } }}>
              {loading && !instances.length && (
                <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
              )}
              {visibleInstances.map((instance) => {
                const relationship = instance.restaurant?.nome
                  || (instance.detected_usages || []).map((usage) => (
                    usage.restaurant_name || usage.product_code
                  )).join(', ')
                  || 'Sem vínculo com restaurante';
                const payment = providerPayment(instance);
                return (
                  <Box key={instance.instance_id} sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.5}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={800} sx={{ color: '#101828', wordBreak: 'break-word' }}>
                          {instance.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {instance.instance_id}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={statusColor(instance.status)}
                        label={STATUS_LABELS[instance.status] || instance.status}
                      />
                    </Stack>
                    <Stack spacing={0.5} mb={1.5}>
                      <Typography variant="body2"><strong>Provedor:</strong> {instance.provider === 'uazapi' ? 'UAZAPI' : 'Z-API'}</Typography>
                      <Typography variant="body2"><strong>Produto:</strong> {instance.product_code}</Typography>
                      <Typography variant="body2" color="text.secondary">{relationship}</Typography>
                    </Stack>
                    <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap mb={2}>
                      <Chip
                        size="small"
                        color={payment.color}
                        variant={payment.variant}
                        label={payment.label}
                      />
                      {providerDueDate(instance) && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Vencimento: ${formatProviderDueDate(instance)}`}
                        />
                      )}
                    </Stack>
                    {renderActions(instance, true)}
                  </Box>
                );
              })}
              {!loading && !visibleInstances.length && (
                <Box sx={{ py: 6, px: 2, textAlign: 'center', color: 'text.secondary' }}>
                  {emptyLocalMessage}
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        <Dialog open={Boolean(qrTarget)} onClose={() => !qrLoading && closeQrCode()} maxWidth="xs" fullWidth>
          <DialogTitle>QR Code da conexão</DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-word' }}>
              {qrTarget?.name || qrTarget?.instance_id}
            </Typography>
            {qrLoading && (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }} role="status">
                <CircularProgress size={34} />
                <Typography color="text.secondary">Solicitando um novo QR Code...</Typography>
              </Stack>
            )}
            {!qrLoading && qrError && <Alert severity="error">{qrError}</Alert>}
            {qrConnected && <Alert severity="success">WhatsApp conectado.</Alert>}
            {!qrLoading && qrImage && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No WhatsApp, abra Aparelhos conectados e selecione Conectar um aparelho.
                </Typography>
                <img
                  src={qrImage}
                  alt="QR Code da instância"
                  style={QR_IMAGE_STYLE}
                  onError={() => {
                    setQrImage(null);
                    setQrError('Não foi possível exibir o QR Code. Gere um novo para tentar novamente.');
                  }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeQrCode} disabled={qrLoading}>Fechar</Button>
            {!qrConnected && (
              <Button variant="contained" disabled={qrLoading} onClick={() => showQrCode(qrTarget)}>Gerar novo QR Code</Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(confirmationTarget)} onClose={() => setConfirmationTarget(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {confirmingSubscription ? confirmingUazapi ? 'Ativar esta conexão UAZAPI?' : 'Assinar esta instância?' : 'Cancelar esta instância?'}
          </DialogTitle>
          <DialogContent>
            <Typography fontWeight={700} sx={{ wordBreak: 'break-word' }}>
              {confirmationTarget?.instance?.name || confirmationTarget?.instance?.instance_id}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, wordBreak: 'break-all' }}>
              {confirmationTarget?.instance?.instance_id}
            </Typography>
            <Alert severity={confirmingSubscription ? 'warning' : 'error'}>
              {confirmingUazapi
                ? confirmingSubscription
                  ? 'A ativação usa uma vaga do plano UAZAPI já contratado. Não cria uma nova assinatura. O restaurante precisa de acesso válido à ferramenta e deverá conectar o WhatsApp pelo QR Code.'
                  : 'A conexão UAZAPI será removida para liberar a vaga. O cadastro, o prompt e as configurações do AtendaBot serão preservados. Uma nova ativação exigirá reconectar o WhatsApp.'
                : confirmingSubscription
                ? 'A assinatura gera cobrança integral no mês atual. Confirme somente se deseja ativar a cobrança desta instância.'
                : 'A Z-API informa que o cancelamento via API mantém a instância ativa até o fim do ciclo vigente, no último dia do mês, com cobrança normal desse período. Não vamos desconectar o WhatsApp. O status e o vencimento serão consultados no provedor para acompanhar o encerramento.'}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setConfirmationTarget(null)}>Voltar</Button>
            <Button
              color={confirmingSubscription ? 'primary' : 'error'}
              variant="contained"
              onClick={confirmInstanceAction}
            >
              {confirmingSubscription ? confirmingUazapi ? 'Confirmar ativação' : 'Confirmar assinatura' : 'Confirmar cancelamento'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(migrationTarget)} onClose={() => !migrationBusy && setMigrationTarget(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Preparar conexão UAZAPI</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography fontWeight={700}>{migrationTarget?.name}</Typography>
              <Alert severity="info">
                Prepare a nova instância, conecte o WhatsApp pelo QR Code e atualize o status.
                A troca só ocorre ao concluir, com o número indicado conectado.
                A criação usa uma vaga disponível do plano fixo UAZAPI, respeitando o acesso do cliente à ferramenta.
                O cadastro, o prompt e as configurações são reaproveitados; basta reconectar o WhatsApp.
                {migrationTarget?.product_code === 'atendabot' && migrationTarget?.restaurant_id
                  ? ' O número é aproveitado da Z-API ou do cadastro. Depois da migração, o cliente pode alterá-lo em Conexão WhatsApp no AtendaBot. '
                  : ' O número é aproveitado da configuração do produto ou da própria instância. '}
                A assinatura Z-API permanece até você solicitar o cancelamento separadamente.
              </Alert>
              {migrationError && <Alert severity="error">{migrationError}</Alert>}
              {migrationStatus && (
                <Alert severity={migrationStatus.ready ? 'success' : 'warning'}>
                  {migrationStatus.migrated ? 'Este restaurante já utiliza a UAZAPI.'
                    : migrationStatus.ready ? 'Número confirmado. A migração pode ser concluída.'
                      : migrationStatus.connected ? 'Número conectado diferente do esperado. A troca está bloqueada.'
                        : `Aguardando conexão do número ${migrationStatus.phone_number}.`}
                </Alert>
              )}
              {migrationImage && (
                <Box sx={{ textAlign: 'center' }}>
                  <img
                    src={migrationImage}
                    alt="QR Code da nova conexão UAZAPI"
                    style={QR_IMAGE_STYLE}
                    onError={() => {
                      setMigrationImage(null);
                      setMigrationError('Não foi possível exibir o QR Code. Selecione Mostrar QR Code para tentar novamente.');
                    }}
                  />
                </Box>
              )}
              {migrationBusy && <CircularProgress size={24} />}
              <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                {!migrationStatus?.migrated && (
                  <Button disabled={migrationBusy} onClick={() => runMigration('prepare')}>Preparar conexão UAZAPI</Button>
                )}
                {!migrationStatus?.migrated && migrationStatus && !migrationStatus.connected && (
                  <Button disabled={migrationBusy} onClick={() => runMigration('qr-code')}>Mostrar QR Code</Button>
                )}
                <Button disabled={migrationBusy} onClick={() => runMigration('status')}>Atualizar status</Button>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={migrationBusy} onClick={() => setMigrationTarget(null)}>Fechar</Button>
            {!migrationStatus?.migrated && (
              <Button variant="contained" disabled={migrationBusy || !migrationStatus?.ready} onClick={() => runMigration('complete')}>
                Concluir migração
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={registerOpen} onClose={() => !registering && setRegisterOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cadastrar instância existente</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              O backend valida o ID e o token na Z-API. As credenciais não serão exibidas novamente.
            </Alert>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Nome para identificação"
                value={registerForm.name}
                onChange={(event) => setRegisterForm((current) => ({ ...current, name: event.target.value }))}
                required
              />
              <TextField
                label="Produto"
                value={registerForm.product_code}
                onChange={(event) => setRegisterForm((current) => ({ ...current, product_code: event.target.value }))}
                helperText="Ex.: atendabot, reservo ou clube_mesa"
                required
              />
              <TextField
                label="ID do restaurante (opcional)"
                type="number"
                value={registerForm.restaurant_id}
                onChange={(event) => setRegisterForm((current) => ({ ...current, restaurant_id: event.target.value }))}
                helperText="Use somente quando a instância pertence ao AtendaBot de um restaurante."
              />
              <TextField
                label="Instance ID"
                value={registerForm.instance_id}
                onChange={(event) => setRegisterForm((current) => ({ ...current, instance_id: event.target.value }))}
                required
              />
              <TextField
                label="Instance Token"
                type="password"
                value={registerForm.instance_token}
                onChange={(event) => setRegisterForm((current) => ({ ...current, instance_token: event.target.value }))}
                required
              />
              <TextField
                label="Vencimento atual (opcional)"
                type="date"
                value={registerForm.legacy_expires_at}
                onChange={(event) => setRegisterForm((current) => ({ ...current, legacy_expires_at: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setRegisterOpen(false)} disabled={registering}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={submitLegacyInstance}
              disabled={registering || !registerForm.name || !registerForm.instance_id || !registerForm.instance_token}
            >
              {registering ? 'Validando...' : 'Cadastrar e validar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ZapiInstancesPage;
